'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Card, CardContent } from '@/components/ui/Card';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-16 lg:py-24">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-primary/5 blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-blue-500/5 blur-3xl pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left Column - Text Content */}
              <div className="space-y-8 max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  Diagnóstico Gratuito
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-secondary leading-tight font-display">
                  Test <span className="text-primary italic">Gratis</span> para<br/>
                  Empresas <span className="text-primary italic">Familiares</span>
                </h1>
                
                <div className="space-y-4">
                  <p className="text-xl text-slate-600 font-medium leading-relaxed">
                    Descubre cuál es el nivel de riesgo de que tu negocio continúe.
                  </p>
                  <p className="text-lg text-slate-500 leading-relaxed">
                    Obtén al menos <strong className="text-secondary font-bold underline decoration-primary decoration-2 underline-offset-2">20 recomendaciones</strong> sobre como mejorar la permanencia de tu negocio en el tiempo.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                  <Link 
                    href="/cuestionario"
                    className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-secondary rounded-full hover:bg-opacity-90 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1"
                  >
                    Comenzar Evaluación
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500 px-4 py-4">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span>100% Confidencial y Seguro</span>
                  </div>
                </div>
              </div>

              {/* Right Column - Mockup/Visualization */}
              <div className="relative lg:h-auto flex items-center justify-center">
                <div className="relative w-full max-w-md lg:max-w-full aspect-[4/3] group">
                  <div className="absolute inset-0 bg-gradient-to-tr from-secondary to-primary rounded-2xl rotate-3 opacity-20 blur-sm transform transition-transform group-hover:rotate-6 duration-700"></div>
                  <div className="absolute inset-0 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col">
                    {/* Browser bar */}
                    <div className="h-8 bg-gray-50 border-b border-gray-100 flex items-center px-4 gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col justify-between relative">
                      <div className="space-y-4">
                        <div className="w-1/3 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-2/3 h-8 bg-secondary/10 rounded animate-pulse"></div>
                        
                        {/* Stats cards */}
                        <div className="flex gap-4 mt-6">
                          <div className="flex-1 p-4 rounded-xl bg-purple-50 border border-purple-100">
                            <div className="text-3xl font-bold text-primary mb-1">85%</div>
                            <div className="text-xs text-slate-500">Score de Viabilidad</div>
                          </div>
                          <div className="flex-1 p-4 rounded-xl bg-blue-50 border border-blue-100">
                            <div className="text-3xl font-bold text-blue-600 mb-1">A+</div>
                            <div className="text-xs text-slate-500">Potencial</div>
                          </div>
                        </div>
                        
                        {/* Chart */}
                        <div className="mt-6 flex items-end gap-2 h-24 pt-4 border-t border-dashed border-gray-200">
                          <div className="w-full bg-primary/20 rounded-t h-[40%]"></div>
                          <div className="w-full bg-primary/40 rounded-t h-[60%]"></div>
                          <div className="w-full bg-primary/60 rounded-t h-[30%]"></div>
                          <div className="w-full bg-primary/80 rounded-t h-[80%]"></div>
                          <div className="w-full bg-primary rounded-t h-[55%]"></div>
                        </div>
                      </div>
                      
                      {/* Notification badge */}
                      <div className="absolute bottom-6 right-6 bg-white p-4 rounded-lg shadow-xl border border-gray-100 flex items-center gap-3 animate-bounce" style={{animationDuration: '3s'}}>
                        <div className="bg-green-100 p-2 rounded-full text-green-600">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-800">Análisis Completado</div>
                          <div className="text-[10px] text-slate-500">Hace 2 minutos</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="py-20 bg-gray-50 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-secondary mb-4 font-display">¿Cómo funciona la evaluación?</h2>
              <p className="text-slate-500 max-w-2xl mx-auto">Nuestro proceso está diseñado para darte claridad inmediata sin complicaciones.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 group">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3 group-hover:text-primary transition-colors">Evaluación Integral</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Análisis completo de 6 áreas clave de competencias profesionales esenciales para el éxito generacional.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3 group-hover:text-primary transition-colors">Resultados Inmediatos</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Sin esperas. Obtén tu perfil de competencias y matriz de riesgos al finalizar el cuestionario.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 group">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3 group-hover:text-primary transition-colors">Enviado por Email</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Recibe tu reporte PDF detallado directamente en tu bandeja de entrada para compartirlo con socios.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary mb-8 font-display">
              ¿Listo para asegurar el futuro de tu legado?
            </h2>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Link 
                href="/cuestionario"
                className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white bg-primary rounded-full hover:bg-opacity-90 transition-all shadow-lg hover:shadow-primary/40"
              >
                Comenzar Evaluación Gratis
              </Link>
              <p className="text-sm text-slate-500 mt-2 sm:mt-0">
                Toma menos de 5 minutos
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}